/**
 * ACO Settings tab (P7.1c, decision D5): Program configuration / Financials /
 * Improvement as SEPARATE cards. IA moved to its own tab; the practice entry
 * template was removed (backlog R5 — returns only as a template+import pair).
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/lib/api-client';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import type { UseAppPlusScoring } from '../lib/useAppPlusScoring';

const TRACKS = ['BASIC_A', 'BASIC_B', 'BASIC_C', 'BASIC_D', 'BASIC_E', 'ENHANCED'] as const;

/** "$1,250,000.50" / "1250000.5" → 1250000.5 (null when empty/garbage). */
const parseMoney = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
/** Display with thousands separators; preserves a partial decimal while typing. */
const fmtMoney = (v: unknown): string => {
  if (v == null || v === '') return '';
  const s = String(v).replace(/[$,\s]/g, '');
  if (!/^\d*\.?\d*$/.test(s)) return String(v);
  const [int, dec] = s.split('.');
  const intFmt = int ? Number(int).toLocaleString('en-US') : '';
  return dec !== undefined ? `${intFmt}.${dec}` : intFmt;
};

export function AcoSettingsTab({ acoId, scoring }: { acoId: number; scoring: UseAppPlusScoring }) {
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const { toast } = useToast();
  // Seed from configRow on first paint — useEffect-only sync painted ENHANCED /
  // empty Gross savings for one frame, then jumped to the real values (CLS).
  const [form, setForm] = useState<Record<string, unknown>>(() => scoring.configRow ?? {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(scoring.configRow ?? {}); }, [scoring.configRow]);

  const set = (k: string, v: unknown) => setForm(prev => ({ ...prev, [k]: v }));

  const payloadFrom = (f: Record<string, unknown>) => ({
    track: f.track ?? 'ENHANCED',
    is_first_year_first_agreement: !!f.is_first_year_first_agreement,
    is_small_practice: !!f.is_small_practice,
    euc_approved: !!f.euc_approved,
    cahps_administered: f.cahps_administered !== false,
    cahps_sampling_exception: !!f.cahps_sampling_exception,
    savings_pool_dollars: parseMoney(f.savings_pool_dollars),
    prior_year_achievement_pct: f.prior_year_achievement_pct === '' || f.prior_year_achievement_pct == null
      ? null : Number(f.prior_year_achievement_pct),
    // IA lives in its own tab (D5) — preserve whatever it saved.
    ia_attestations: Array.isArray(f.ia_attestations) ? f.ia_attestations : [],
  });

  /** Instant auto-save (owner 2026-07-20 — MIPS Configuration parity, no button). */
  const saveNow = async (f: Record<string, unknown>) => {
    setSaving(true);
    try {
      await apiClient.put(`/api/acos/${acoId}/config?year=${programYear}`, payloadFrom(f));
      await scoring.refresh();
    } catch (e) {
      toast({ title: 'Save failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  /** Toggles/selects save the moment they change. */
  const setAndSave = (k: string, v: unknown) => {
    const next = { ...form, [k]: v };
    setForm(next);
    void saveNow(next);
  };

  const boolRow = (key: string, label: string, hint?: string) => (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div>
        <Label className="font-medium">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch
        checked={!!form[key]}
        disabled={isCurrentYearLocked}
        onCheckedChange={(v) => setAndSave(key, v)}
      />
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Program configuration</CardTitle>
          <CardDescription>Track and per-year flags (admin-managed)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>MSSP track</Label>
            <Select
              value={String(form.track ?? 'ENHANCED')}
              onValueChange={(v) => setAndSave('track', v)}
              disabled={isCurrentYearLocked}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRACKS.map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {boolRow('is_first_year_first_agreement', 'First year of first agreement period', 'QPS pathway 3 eligibility')}
          {boolRow('is_small_practice', 'Small practice', '≤15 clinicians at the APM Entity level — enables the 3-point data-completeness floor and the +6 bonus')}
          {boolRow('euc_approved', 'EUC override approved (§425.512(c))', 'Floors the quality score at 73.85')}
          {boolRow('cahps_administered', 'CAHPS administered')}
          {boolRow('cahps_sampling_exception', 'CAHPS sampling exception approved')}
        </CardContent>
      </Card>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Financials</CardTitle>
            <CardDescription>Inputs for the shared-savings panel</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>Gross savings</Label>
              <p className="text-xs text-muted-foreground">
                Total savings against the benchmark, before the sharing rate —
                projected shared savings = gross savings × effective sharing rate
              </p>
              {/* Dollar-formatted entry (owner 2026-07-20): commas render as you
                  type; "$" and commas are stripped before saving. */}
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="text" inputMode="decimal" className="pl-7" disabled={isCurrentYearLocked}
                  placeholder="0"
                  value={fmtMoney(form.savings_pool_dollars)}
                  onChange={(e) => set('savings_pool_dollars', e.target.value)}
                  onBlur={() => saveNow(form)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Improvement score</CardTitle>
            <CardDescription>Optional prior-year input — up to +10 bonus points</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>Prior-year achievement %</Label>
              <Input
                type="number" min={0} max={100} step="0.01" disabled={isCurrentYearLocked}
                value={(form.prior_year_achievement_pct as number | string | null) ?? ''}
                onChange={(e) => set('prior_year_achievement_pct', e.target.value)}
                onBlur={() => saveNow(form)}
              />
              <p className="text-xs text-muted-foreground">
                CMS eligibility rule (not enforced here): improvement credit requires full
                participation with data completeness met in BOTH years under comparable
                identifiers — leave empty if that doesn't hold.
              </p>
            </div>
            {/* Auto-save (owner 2026-07-20): toggles/selects save on change, dollar
                and % fields on blur — no Save button, matching MIPS Configuration. */}
            <p className="text-right text-xs text-muted-foreground" aria-live="polite">
              {saving ? 'Saving…' : 'Changes save automatically'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
